import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
  GooglePay,
  PaymentData,
  PaymentAction,
  SubmitActions,
  UIElement,
  AdditionalDetailsData,
  AdyenCheckoutError,
} from '@adyen/adyen-web';

import {
  ESdkEvents,
  MixpanelEvents,
  PaymentMethod,
  EPaymentProvider,
  AppchargeErrorType,
  EBEPaymentMethodsName,
  GetAdyenPaymentStatusResponse,
} from '../../../../../../../contracts';
import { sendFEEvent } from '../../../../../../../eventUtil';
import { useAPI } from '../../../../../../../services/api.service';
import { useCheckout } from '../../../../../../../hooks/use-checkout';
import { usePaymentState } from '../../../../../hooks/use-payment-state';
import { AdyenFinalizePaymentResponse } from '../../../../../../../contracts';
import { useAdyenCheckout } from '../../../../AdyenPaymentElement/hooks/use-adyen-checkout';
import { ADYEN_CARD_CONFIGURATION } from '../../../../AdyenPaymentElement/utils';
import { getPaymentRequestAmount } from '../../../../../../../utils/get-payment-request-amount';
import { generateAppchargeErrorMessage } from '../../../../../../../utils/generate-appcharge-error-message';
import { useOrderPolling } from '../../../../../hooks/use-order-polling';
import { apmLatencyTracker } from '../../../../../../../utils/apm-latency-tracker';
import { sendMixpanelEvent } from '../../../../../../../utils/mixpanel-utils';
import { logError } from '../../../../../../../utils/coralogix-rum';
import {
  AdyenCheckoutErrorTypes,
  mapAdyenSdkErrorToAppchargeError,
} from '../../AdyenApplePay/utils';

export function UseAdyenGooglePay(onReady: (isAvailable: boolean) => void) {
  const isAdyenPaymentInProgress = useRef(false);
  const isPaySessionStartingRef = useRef(false);
  const pendingErrorTypeRef = useRef<AppchargeErrorType | undefined>(undefined);
  const pendingActionRef = useRef<PaymentAction | null>(null);
  const [googlePay, setGooglePay] = useState<UIElement | null>(null);
  const [threeDSComponent, setThreeDSComponent] = useState<UIElement | null>(null);
  const submitParamsRef = useRef<{ email: string }>({ email: '' });
  const { checkoutData, getEventParams, getMixpanelEventParams } = useCheckout();
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);
  const { startOrderPolling } = useOrderPolling();
  const { API } = useAPI();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const googlePayPM = useMemo(
    () =>
      checkoutData?.paymentMethods.find(
        (pm) =>
          pm.provider === EPaymentProvider.adyen &&
          pm.paymentMethodName === EBEPaymentMethodsName.google,
      ),
    [checkoutData?.paymentMethods],
  );
  const { checkoutInstance } = useAdyenCheckout(googlePayPM);

  const gatewayMerchantId = googlePayPM?.publicConfig?.merchantId || '';
  const configuration = googlePayPM?.paymentMethodConfig?.configuration || {};

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Google Pay', googlePayPM?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }

    let googlePay: GooglePay;
    if (checkoutInstance) {
      googlePay = new GooglePay(checkoutInstance, {
        callbackIntents: [],
        showPayButton: true,
        isExpress: true,
        configuration: {
          gatewayMerchantId,
          ...configuration,
        },
        onAuthorized: (data, actions) => {
          submitParamsRef.current.email =
            data.authorizedEvent.email || checkoutData?.customer.email || '';
          actions.resolve();
        },
        onError: (error) => {
          console.log('onError Google Pay', { error });
          if (error.name === AdyenCheckoutErrorTypes.CANCEL) {
            handleCancel();
            return;
          }
          // Prefer the BE-reported error type if one was stashed by handleServerResponse;
          // otherwise fall back to mapping the Adyen SDK error name (ACDEV-24170).
          const errorType =
            pendingErrorTypeRef.current ?? mapAdyenSdkErrorToAppchargeError(error.name);
          pendingErrorTypeRef.current = undefined;
          handleError(errorType, error);
        },
        onPaymentCompleted,
        onPaymentFailed,
        onClick: (resolve) => {
          if (isPaySessionStartingRef.current) {
            resolve();
            return;
          }
          isPaySessionStartingRef.current = true;
          sendFEEvent(ESdkEvents.ON_PAYMENT_INTENT, {
            ...getEventParams(),
            paymentMethodName: googlePayPM?.paymentMethodDisplayName,
          });
          sendMixpanelEvent(MixpanelEvents.ON_PAY_BUTTON_CLICKED, {
            ...getMixpanelEventParams(),
            paymentMethodName: googlePayPM?.paymentMethodDisplayName,
          });
          resolve();
        },
        onSubmit: async (state, _, actions) => {
          if (isAdyenPaymentInProgress.current) {
            return;
          }
          isAdyenPaymentInProgress.current = true;
          setIsPaymentLoading(true);
          try {
            const finalizePaymentResponse = await API.finalizePayment<AdyenFinalizePaymentResponse>(
              createGooglePayFinalizeBody(googlePayPM!, state.data, submitParamsRef.current.email),
            );
            handleServerResponse(finalizePaymentResponse, actions);
          } catch {
            pendingErrorTypeRef.current = undefined;
            actions.reject();
          }
        },

        buttonSizeMode: 'fill',
        buttonRadius: 5,
        emailRequired: true,
        transactionInfo: {
          displayItems: [],
          countryCode: checkoutData?.customer?.geoLocator?.countryCode2,
          currencyCode: checkoutData?.pricing.currencyCode,
          totalPriceStatus: 'FINAL',
          totalPrice: getPaymentRequestAmount(priceBreakdown?.priceTotal, checkoutData?.pricing),
          totalPriceLabel: 'Total',
        },
      });

      googlePay
        ?.isAvailable()
        .then(() => onReady?.(true))
        .catch(() => {
          setGooglePay(null);
          onReady?.(false);
        })
        .finally(() => {
          try {
            apmLatencyTracker.endTracking('Google Pay', googlePayPM?.id, {
              paymentMethodName: googlePayPM?.paymentMethodName,
              paymentMethodDisplayName: googlePayPM?.paymentMethodDisplayName,
              provider: 'Adyen',
              type: 'Express',
            });
          } catch (error) {
            console.error('Failed to end APM latency tracking:', error);
          }
        });

      setGooglePay(googlePay);
    }
  }, [checkoutInstance, priceBreakdown]);

  const onPaymentCompleted = () => {
    // When the BE responded with status: 'redirect' + action, we resolved
    // the GPay sheet WITHOUT the action so it would close. The SDK then
    // fires onPaymentCompleted prematurely; mount our 3DS modal here
    // instead of treating this as a real success.
    if (pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      handle3DSFlow(action);
      return;
    }
    handleSuccess();
  };

  const handleSuccess = () => {
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
      ...getEventParams(),
      paymentMethodName: googlePayPM?.paymentMethodDisplayName,
    });
    isPaySessionStartingRef.current = false;
    startOrderPolling(checkoutData?.orderId!, {
      paymentMethodName: googlePayPM?.paymentMethodDisplayName,
    });
  };

  const onPaymentFailed = () => {
    handleError();
  };

  const handleCancel = () => {
    setIsPaymentLoading(false);
    setThreeDSComponent(null);
    pendingActionRef.current = null;
    isAdyenPaymentInProgress.current = false;
    isPaySessionStartingRef.current = false;
  };

  const handleServerResponse = (
    finalizePaymentResponse: AdyenFinalizePaymentResponse,
    actions: SubmitActions,
  ) => {
    const { status, resultCode, action } = finalizePaymentResponse.adyen;
    if (status === 'succeeded') {
      actions.resolve({ resultCode: resultCode, action });
    } else if (status === 'redirect') {
      if (action) {
        // Stash the action and resolve WITHOUT it. The Adyen SDK will close
        // the Google Pay sheet, then fire onPaymentCompleted where we mount
        // our own 3DS modal (separate instance, same approach as the card flow).
        pendingActionRef.current = action;
      }
      actions.resolve({ resultCode });
    } else if (status === 'failed') {
      pendingErrorTypeRef.current = finalizePaymentResponse.appChargeErrorType;
      actions.reject();
    }
  };

  const handle3DSFlow = (action: PaymentAction) => {
    const actionComponent = checkoutInstance!.createFromAction(action, {
      ...ADYEN_CARD_CONFIGURATION,
      onAdditionalDetails,
    });
    setThreeDSComponent(actionComponent);
  };

  const onAdditionalDetails = async (state: AdditionalDetailsData) => {
    const threeDSResult = state.data.details.threeDSResult;
    setThreeDSComponent(null);

    if (!threeDSResult) {
      handleError();
      return;
    }

    try {
      const paymentStatusResponse = await API.getPaymentStatus_NEW<GetAdyenPaymentStatusResponse>(
        threeDSResult,
        googlePayPM?.id as string,
      );
      if (paymentStatusResponse.resultCode === 'Authorised') {
        handleSuccess();
      } else {
        handleError();
      }
    } catch {
      handleError();
    }
  };

  const handleError = (appChargeErrorType?: AppchargeErrorType, sdkError?: AdyenCheckoutError) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    if (sdkError) {
      logError('Adyen Google Pay error', sdkError, {
        paymentMethodId: googlePayPM?.id,
        paymentMethodName: googlePayPM?.paymentMethodName,
        paymentMethodDisplayName: googlePayPM?.paymentMethodDisplayName,
        sdkErrorName: sdkError.name,
        appChargeErrorType,
      });
    }
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
      ...getEventParams(),
      paymentMethodName: googlePayPM?.paymentMethodDisplayName,
    });
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
    setThreeDSComponent(null);
    pendingActionRef.current = null;
    isAdyenPaymentInProgress.current = false;
    isPaySessionStartingRef.current = false;
  };

  return {
    googlePayRef: googlePay,
    threeDSComponent,
  };
}

function createGooglePayFinalizeBody(
  selectedPaymentMethod: PaymentMethod,
  adyenData: PaymentData,
  email: string,
) {
  return {
    paymentProvider: EPaymentProvider.adyen,
    receipt_email: email,
    paymentMethodDisplayName: selectedPaymentMethod.paymentMethodDisplayName,
    paymentMethodId: selectedPaymentMethod.id,
    params: {
      browserInfo: adyenData.browserInfo,
      paymentMethod: adyenData.paymentMethod,
    },
  };
}
